import * as yup from "yup";
import { DynamicQuestionAnswerInput, DynamicQuestionAnswerRecord } from "@interfaces/response";
import { DynamicQuestionCatalogItem } from "@interfaces/catalogs";
import { DynamicQuestionAnswerItem } from "@interfaces/request";

// { [code]: raw } -> [{ question_id, value }] para POST solicitudes/{id}/dynamic_question.
// Se manda "question_id" (el id del catálogo, no el "code") y solo se incluyen las preguntas
// contestadas (sin valor no se manda nada — coincide con el ejemplo real del endpoint, que no
// incluye ahí las preguntas sin responder).
export const buildDynamicQuestionAnswerItems = (
  questions: DynamicQuestionCatalogItem[],
  values: Record<string, DynamicQuestionAnswerInput>,
): DynamicQuestionAnswerItem[] =>
  questions.reduce<DynamicQuestionAnswerItem[]>((items, question) => {
    const raw = values[question.code];
    const isEmpty = question.type === "multiple_choice" ? !Array.isArray(raw) || raw.length === 0 : !raw;
    if (isEmpty) return items;

    items.push({
      question_id: question.id,
      value: Array.isArray(raw) ? raw : (raw as string),
    });
    return items;
  }, []);

// GET solicitudes/{id}/dynamic_question -> { [code]: value }, para precargar
// formik.values.dynamicAnswers. La respuesta viene indexada por "question_id" (id del
// catálogo), así que se cruza contra "questions" para saber a qué "code" corresponde cada una;
// si una respuesta ya no corresponde a ninguna pregunta del catálogo/step actual, se ignora.
export const mapDynamicQuestionAnswerRecordsToFormValues = (
  records: DynamicQuestionAnswerRecord[],
  questions: DynamicQuestionCatalogItem[],
): Record<string, DynamicQuestionAnswerInput> =>
  records.reduce<Record<string, DynamicQuestionAnswerInput>>((acc, record) => {
    const question = questions.find((item) => item.id === record.question_id);
    if (!question) return acc;

    acc[question.code] = Array.isArray(record.value) ? record.value.map(String) : String(record.value ?? "");
    return acc;
  }, {});

// Evalúa si una pregunta se debe mostrar según "visible_when" (depende de la respuesta de
// otra pregunta). Si no trae "visible_when", siempre es visible. Si el operador no se
// reconoce, se muestra igual (más seguro que ocultar por error).
export const isDynamicQuestionVisible = (
  question: DynamicQuestionCatalogItem,
  answers: Record<string, DynamicQuestionAnswerInput>,
): boolean => {
  const condition = question.visible_when;
  if (!condition) return true;

  const currentValue = answers[condition.question_code];

  if (condition.operator === "eq") {
    return currentValue === condition.value;
  }

  return true;
};

// Yup para "formik.values.dynamicAnswers": aplica "required" (y min/max para multiple_choice)
// solo cuando la pregunta está visible según "visible_when" — una pregunta oculta nunca bloquea
// el submit, y una que se vuelve visible (ej. "especifica_pagos_anticipados" al responder "Si"
// en "pagos_anticipados") sí se valida como requerida.
export const buildDynamicAnswersValidationSchema = (questions: DynamicQuestionCatalogItem[]) => {
  const shape: Record<string, yup.AnySchema> = {};

  questions.forEach((question) => {
    const isMultiple = question.type === "multiple_choice";
    let schema: yup.AnySchema = isMultiple ? yup.array().of(yup.string()) : yup.string();

    if (isMultiple) {
      schema = (schema as yup.ArraySchema<(string | undefined)[], yup.AnyObject>)
        .test("min-selections", `Selecciona al menos ${question.min_selections}`, (value) =>
          question.min_selections ? (value?.length ?? 0) >= question.min_selections : true,
        )
        .test("max-selections", `Selecciona como máximo ${question.max_selections}`, (value) =>
          question.max_selections ? (value?.length ?? 0) <= question.max_selections : true,
        );
    }

    const applyRequired = (s: yup.AnySchema) => {
      if (!question.required) return s.notRequired();
      return isMultiple
        ? (s as yup.ArraySchema<(string | undefined)[], yup.AnyObject>).min(1, "Campo requerido")
        : s.required("Campo requerido");
    };

    if (question.visible_when) {
      const { question_code, operator, value: expectedValue } = question.visible_when;
      schema = schema.when(question_code, {
        is: (siblingValue: unknown) => (operator === "eq" ? siblingValue === expectedValue : true),
        then: (s: yup.AnySchema) => applyRequired(s),
        otherwise: (s: yup.AnySchema) => s.notRequired(),
      });
    } else {
      schema = applyRequired(schema);
    }

    shape[question.code] = schema;
  });

  return yup.object().shape(shape);
};
